import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, BookOpen, Loader2, Mail, Phone, Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Teachers = () => {
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const fileInputRef = useRef(null);
    const [editingTeacher, setEditingTeacher] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        arabic_name: '',
        date_of_birth: '',
        ppsn: '',
        address: '',
        mobile: '',
        personal_email: '',
        work_email: '',
        bank_iban: '',
        hourly_rate: 0
    });

    useEffect(() => {
        fetchTeachers();
    }, []);

    const fetchTeachers = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/teachers`, { withCredentials: true });
            setTeachers(response.data);
        } catch (error) {
            toast.error('Failed to load teachers');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        // Convert empty personal_email '' → null so Pydantic Optional[EmailStr] accepts it
        const payload = { ...formData, personal_email: formData.personal_email?.trim() ? formData.personal_email.trim() : null };
        try {
            if (editingTeacher) {
                await axios.put(`${API_URL}/api/teachers/${editingTeacher.id}`, payload, { withCredentials: true });
                toast.success('Teacher updated');
            } else {
                await axios.post(`${API_URL}/api/teachers`, payload, { withCredentials: true });
                toast.success('Teacher created');
            }
            setDialogOpen(false);
            resetForm();
            fetchTeachers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save teacher');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (teacher) => {
        setEditingTeacher(teacher);
        setFormData({
            first_name: teacher.first_name,
            last_name: teacher.last_name,
            arabic_name: teacher.arabic_name || '',
            date_of_birth: teacher.date_of_birth || '',
            ppsn: teacher.ppsn || '',
            address: teacher.address || '',
            mobile: teacher.mobile || '',
            personal_email: teacher.personal_email || '',
            work_email: teacher.work_email || '',
            bank_iban: teacher.bank_iban || '',
            hourly_rate: teacher.hourly_rate || 0
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this teacher?')) return;
        try {
            await axios.delete(`${API_URL}/api/teachers/${id}`, { withCredentials: true });
            toast.success('Teacher deleted');
            fetchTeachers();
        } catch (error) {
            toast.error('Failed to delete teacher');
        }
    };

    const resetForm = () => {
        setEditingTeacher(null);
        setFormData({
            first_name: '', last_name: '', arabic_name: '', date_of_birth: '', ppsn: '',
            address: '', mobile: '', personal_email: '', work_email: '', bank_iban: '', hourly_rate: 0
        });
    };

    const filteredTeachers = teachers.filter(t =>
        t.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.work_email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="teachers-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Teachers</h1>
                        <p className="text-[#4B5563]">Manage school teachers</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" onClick={() => { setUploadResult(null); setUploadDialogOpen(true); }} data-testid="upload-teachers-btn"><Upload className="h-4 w-4 mr-2" /> Upload Excel</Button>
                        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="add-teacher-btn">
                            <Plus className="h-4 w-4 mr-2" /> Add Teacher
                        </Button>
                    </div>
                </div>

                <Card className="card-surface">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                            <Input placeholder="Search teachers..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="card-surface">
                    <CardContent className="p-0">
                        {filteredTeachers.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Teacher</TableHead>
                                            <TableHead>Contact</TableHead>
                                            <TableHead>Hourly Rate</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredTeachers.map((teacher) => (
                                            <TableRow key={teacher.id}>
                                                <TableCell>
                                                    <div className="flex items-center space-x-3">
                                                        <div className="h-10 w-10 rounded-full bg-[#064E3B] flex items-center justify-center text-white font-medium">
                                                            {teacher.first_name?.[0]}{teacher.last_name?.[0]}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium">{teacher.first_name} {teacher.last_name}</p>
                                                            <p className="text-xs text-[#4B5563]">{teacher.arabic_name}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="space-y-1">
                                                        <div className="flex items-center text-sm">
                                                            <Mail className="h-3 w-3 mr-1 text-[#4B5563]" />
                                                            {teacher.work_email}
                                                        </div>
                                                        <div className="flex items-center text-sm text-[#4B5563]">
                                                            <Phone className="h-3 w-3 mr-1" />
                                                            {teacher.mobile}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className="bg-[#10B981] tabular-nums">€{teacher.hourly_rate?.toFixed(2)}/hr</Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end space-x-2">
                                                        <Button size="sm" variant="outline" onClick={() => handleEdit(teacher)}>
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="text-[#EF4444]" onClick={() => handleDelete(teacher.id)}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12">
                                <BookOpen className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                <p className="text-[#4B5563] mb-4">No teachers found</p>
                                <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]">
                                    <Plus className="h-4 w-4 mr-2" /> Add First Teacher
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">{editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Work Email <span className="text-[#EF4444]">*</span></Label>
                                <Input type="email" value={formData.work_email} onChange={(e) => setFormData({...formData, work_email: e.target.value})} required data-testid="teacher-work-email" placeholder="e.g. teacher@nhq.ie" />
                                <p className="text-xs text-[#6B7280]">Only the work email is required — everything else is optional and can be filled in later.</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>First Name</Label>
                                    <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Last Name</Label>
                                    <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Arabic Name</Label>
                                    <Input value={formData.arabic_name} onChange={(e) => setFormData({...formData, arabic_name: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Date of Birth</Label>
                                    <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>PPSN</Label>
                                    <Input value={formData.ppsn} onChange={(e) => setFormData({...formData, ppsn: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Mobile</Label>
                                    <Input value={formData.mobile} onChange={(e) => setFormData({...formData, mobile: e.target.value})} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Address</Label>
                                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                                <Label>Personal Email</Label>
                                <Input type="email" value={formData.personal_email} onChange={(e) => setFormData({...formData, personal_email: e.target.value})} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Bank IBAN</Label>
                                    <Input value={formData.bank_iban} onChange={(e) => setFormData({...formData, bank_iban: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Hourly Rate (€)</Label>
                                    <Input type="number" step="0.01" value={formData.hourly_rate} onChange={(e) => setFormData({...formData, hourly_rate: parseFloat(e.target.value) || 0})} />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    {editingTeacher ? 'Update' : 'Create'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Upload Dialog */}
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader><DialogTitle className="font-['Outfit'] flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-[#064E3B]" /> Upload Teachers Excel</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-4 text-sm text-[#166534]"><p className="font-medium mb-1">Required columns:</p><p>first_name, last_name, work_email</p><p className="mt-1 text-xs">Optional: arabic_name, date_of_birth, ppsn, address, mobile, personal_email, bank_iban, hourly_rate</p></div>
                            <Button variant="outline" className="w-full" onClick={async () => { try { const r = await axios.get(`${API_URL}/api/teachers/upload/template`, { withCredentials: true, responseType: 'blob' }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.setAttribute('download', 'teachers_template.xlsx'); document.body.appendChild(a); a.click(); a.remove(); } catch { toast.error('Failed'); } }} data-testid="download-teachers-template"><Download className="h-4 w-4 mr-2" /> Download Template</Button>
                            <div className="border-2 border-dashed border-[#D1D5DB] rounded-lg p-6 text-center">
                                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={async (e) => { const file = e.target.files[0]; if (!file) return; setUploading(true); setUploadResult(null); const fd = new FormData(); fd.append('file', file); try { const r = await axios.post(`${API_URL}/api/teachers/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }); setUploadResult(r.data); if (r.data.created > 0) { toast.success(`${r.data.created} teacher(s) imported`); fetchTeachers(); } } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; } }} className="hidden" id="teachers-file-upload" />
                                <label htmlFor="teachers-file-upload" className="cursor-pointer">{uploading ? <div className="flex flex-col items-center"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B] mb-2" /><p className="text-sm">Uploading...</p></div> : <div className="flex flex-col items-center"><Upload className="h-8 w-8 text-[#4B5563] mb-2" /><p className="text-sm font-medium">Click to upload</p></div>}</label>
                            </div>
                            {uploadResult && (<div className="space-y-2"><div className="flex items-center gap-2 text-sm"><CheckCircle className="h-4 w-4 text-[#10B981]" /><span>{uploadResult.created} created</span></div>{uploadResult.skipped > 0 && <div className="flex items-center gap-2 text-sm text-[#F59E0B]"><AlertCircle className="h-4 w-4" /><span>{uploadResult.skipped} skipped</span></div>}{uploadResult.errors?.length > 0 && <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3 max-h-32 overflow-y-auto">{uploadResult.errors.map((er, i) => <p key={`err-${i}-${er.slice(0,30)}`} className="text-xs text-[#DC2626]">{er}</p>)}</div>}</div>)}
                        </div>
                        <DialogFooter><Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Close</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default Teachers;
